import {isNil, last} from 'ramda';

import {Nullish} from 'shared/web';

// Quill's stylesheet zeroes their margin in the editor; emails have no stylesheet, so it is baked in.
// `ul`, `ol` and `li` are absent because the editor leaves their margin at the client default too.
const EMAIL_BLOCK_ELEMENTS = 'p, h1, h2, h3, h4, h5, h6, blockquote';

// Editor chrome rendered inside every list item, never content.
const QUILL_LIST_UI_SELECTOR = 'span.ql-ui';

// Quill 2 does not nest lists; it marks an indented item with a class and indents it from its own
// stylesheet, 3em per level.
const QUILL_INDENT_CLASS_REGEX = /^ql-indent-(\d+)$/;
const QUILL_INDENT_WIDTH_IN_EM = 3;

const REFUSED_ELEMENTS =
  'script, style, iframe, frame, object, embed, form, input, button, link, meta, base, noscript, template, svg, math';
const EVENT_HANDLER_ATTRIBUTE_PREFIX = 'on';
const URL_SCHEME_REGEX = /^\s*([a-z][a-z0-9+.-]*):/i;
// A browser strips these from a URL before parsing its scheme, so `jav<tab>ascript:` resolves to
// `javascript:`. A value carrying any of them is refused rather than repaired.
const URL_CONTROL_CHARACTERS_REGEX = /[\t\n\r]/g;
const ALLOWED_LINK_SCHEMES = ['http', 'https', 'mailto', 'tel'];
const ALLOWED_MEDIA_SCHEMES = ['http', 'https'];
const ALLOWED_SIGNATURE_MEDIA_SCHEMES = [...ALLOWED_MEDIA_SCHEMES, 'data'];

// What an author types into the link dialog instead of a full address, and the scheme each shape
// needs. An e-mail address is recognised first, because it carries a dot as well.
const SCHEMELESS_HREF_REPAIRS = [
  {pattern: /^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/, scheme: 'mailto:'},
  {pattern: /^[^\s/?#@]+\.[^\s/?#@]+/, scheme: 'https://'},
];

// Nothing turns a written-out address into a link on its own: Quill has no autolink, and a message
// template arrives as plain text. An explicit scheme counts, and so does the `www.` people write
// instead of one; a bare `example.com` does not, because ordinary prose is full of dotted words.
const PLAIN_TEXT_URL_REGEX = /(?:https?:\/\/|www\.)[^\s<]+/gi;
// A URL that ends a sentence must not swallow the punctuation.
const URL_TRAILING_PUNCTUATION_REGEX = /[.,;:!?'"]+$/;
// Characters that can carry on an address, so a match starting after one is inside a longer token.
const TOKEN_CHARACTER_REGEX = /[\w@.+%-]/;
const CLOSING_BRACKET_PAIRS: Record<string, string> = {')': '(', ']': '[', '}': '{'};

// Mirrors `InlineStyleSanitizer` in the backend.
const ALLOWED_STYLE_PROPERTIES = [
  'text-align',
  'margin',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'padding',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'width',
  'height',
  'max-width',
  'color',
  'background-color',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'line-height',
  'text-decoration',
  'vertical-align',
];

// Each can fetch a remote resource, execute in an old client, or escape its declaration.
const REFUSED_STYLE_VALUE_FRAGMENTS = ['url(', 'expression(', '@import', '\\', '&#', '/*', '*/'];

export interface NormalizeEmailHtmlOptions {
  /** Keep `data:` image sources. Only the signature's backend sanitizer allows them. */
  allowsDataImages?: boolean;
}

/**
 * Turns what the rich text editor emits into markup an email client renders the same way,
 * completes the links the author wrote without a scheme, and refuses what the backend sanitizer
 * refuses.
 *
 * Not every body reaching this came from the editor — a stored draft or a rendered template can
 * carry anything — so the author never previews something the customer cannot receive. The backend
 * stays the boundary; this keeps the two in step.
 */
export function normalizeEmailHtml(
  value: string | Nullish,
  options: NormalizeEmailHtmlOptions = {}
): string {
  if (isNil(value) || value.trim() === '') {
    return '';
  }

  const mediaSchemes = options.allowsDataImages
    ? ALLOWED_SIGNATURE_MEDIA_SCHEMES
    : ALLOWED_MEDIA_SCHEMES;

  const {body} = new DOMParser().parseFromString(value, 'text/html');

  body.querySelectorAll(REFUSED_ELEMENTS).forEach((element) => element.remove());
  linkPlainTextUrls(body);
  body.querySelectorAll('*').forEach((element) => refuseUnsafeAttributes(element, mediaSchemes));
  body.querySelectorAll(QUILL_LIST_UI_SELECTOR).forEach((element) => element.remove());
  body.querySelectorAll('ol').forEach(convertQuillListToSemanticLists);

  body.querySelectorAll(EMAIL_BLOCK_ELEMENTS).forEach((element) => {
    if (element instanceof HTMLElement) {
      element.style.margin = '0';
    }
  });

  // After the margin baking, which resets `margin-left`.
  body.querySelectorAll('[class]').forEach(convertQuillIndentToInlineMargin);

  return body.innerHTML;
}

function refuseUnsafeAttributes(element: Element, mediaSchemes: string[]) {
  element
    .getAttributeNames()
    .filter((name) => name.toLowerCase().startsWith(EVENT_HANDLER_ATTRIBUTE_PREFIX))
    .forEach((name) => element.removeAttribute(name));

  // A refused `href` costs only the link; a refused `src` would leave a broken image, so the
  // element goes with it.
  repairSchemelessHref(element);
  refuseUnsafeUrl(element, 'href', ALLOWED_LINK_SCHEMES, 'attribute');
  refuseUnsafeUrl(element, 'src', mediaSchemes, 'element');
  refuseUnsafeUrl(element, 'srcset', mediaSchemes, 'element');

  refuseUnsafeStyleDeclarations(element);
}

function refuseUnsafeUrl(
  element: Element,
  attribute: string,
  allowedSchemes: string[],
  refuse: 'attribute' | 'element'
) {
  const url = element.getAttribute(attribute);
  if (isNil(url)) {
    return;
  }

  const cleaned = url.replace(URL_CONTROL_CHARACTERS_REGEX, '');
  // `srcset` is a candidate list, so every candidate's scheme is checked, not just the first.
  const urls = attribute === 'srcset' ? cleaned.split(',') : [cleaned];
  const isAllowed =
    cleaned === url && urls.every((candidate) => carriesAllowedScheme(candidate, allowedSchemes));

  if (isAllowed) {
    return;
  }

  if (refuse === 'element') {
    element.remove();
    return;
  }

  element.removeAttribute(attribute);
}

// A relative URL or an anchor carries no scheme and is left alone.
function carriesAllowedScheme(url: string, allowedSchemes: string[]) {
  const scheme = URL_SCHEME_REGEX.exec(url.trim())?.[1]?.toLowerCase();

  return isNil(scheme) || allowedSchemes.includes(scheme);
}

/**
 * Completes or drops a link written without a scheme.
 *
 * Quill stores the value exactly as it was typed, so `www.example.com` arrives with no scheme. The
 * backend sanitizer drops such an `href` — Symfony's `allowRelativeLinks` is off — which leaves an
 * `<a>` with nothing to open: no link in the preview and none in the delivered e-mail. The two
 * shapes an author writes are completed here; anything else relative has nothing to resolve
 * against in a mail client, so it goes the way the backend would take it.
 */
function repairSchemelessHref(element: Element) {
  const href = element.getAttribute('href')?.trim();

  if (isNil(href) || !isNil(URL_SCHEME_REGEX.exec(href))) {
    return;
  }

  const repair = SCHEMELESS_HREF_REPAIRS.find(({pattern}) => pattern.test(href));

  if (isNil(repair)) {
    element.removeAttribute('href');
    return;
  }

  element.setAttribute('href', `${repair.scheme}${href}`);
}

function linkPlainTextUrls(body: HTMLElement) {
  const walker = body.ownerDocument.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];

  // Collected before anything is replaced: swapping the node the walker sits on ends the walk.
  while (walker.nextNode()) {
    const node = walker.currentNode;

    if (node instanceof Text && isNil(node.parentElement?.closest('a'))) {
      textNodes.push(node);
    }
  }

  textNodes.forEach((node) => linkUrlsInTextNode(node, body.ownerDocument));
}

function linkUrlsInTextNode(node: Text, ownerDocument: Document) {
  const text = node.data;
  const matches = Array.from(text.matchAll(PLAIN_TEXT_URL_REGEX)).filter((match) =>
    startsAToken(text, match.index)
  );

  if (matches.length === 0) {
    return;
  }

  const fragment = ownerDocument.createDocumentFragment();
  let cursor = 0;

  matches.forEach((match) => {
    const url = trimUrlPunctuation(match[0]);
    const anchor = ownerDocument.createElement('a');

    // A `www.` address carries no scheme; the attribute pass completes it with
    // `repairSchemelessHref`.
    anchor.setAttribute('href', url);
    anchor.textContent = url;

    fragment.append(text.slice(cursor, match.index), anchor);
    cursor = match.index + url.length;
  });

  fragment.append(text.slice(cursor));
  node.replaceWith(fragment);
}

/**
 * `foo.www.example.com` matches from `www.`, which would show the whole address and link only its
 * tail: the reader is told one host and sent to another.
 */
function startsAToken(text: string, index: number) {
  return index === 0 || !TOKEN_CHARACTER_REGEX.test(text.charAt(index - 1));
}

function trimUrlPunctuation(url: string) {
  let trimmed = url.replace(URL_TRAILING_PUNCTUATION_REGEX, '');

  while (closesAnUnopenedBracket(trimmed)) {
    trimmed = trimmed.slice(0, -1).replace(URL_TRAILING_PUNCTUATION_REGEX, '');
  }

  return trimmed;
}

/**
 * A closing bracket belongs to the address only when the address opened one: `(https://x.com)`
 * wraps the address, `…/Skoda_(company)` and `…/path[1]` are part of it.
 */
function closesAnUnopenedBracket(url: string) {
  const closing = url.slice(-1);
  const opening = CLOSING_BRACKET_PAIRS[closing];

  return !isNil(opening) && occurrences(url, opening) < occurrences(url, closing);
}

function occurrences(text: string, character: string) {
  return text.split(character).length - 1;
}

function refuseUnsafeStyleDeclarations(element: Element) {
  const style = element.getAttribute('style');
  if (isNil(style)) {
    return;
  }

  const kept: string[] = [];

  for (const declaration of style.split(';')) {
    const [rawProperty, ...rawValue] = declaration.split(':');
    const property = rawProperty.trim().toLowerCase();
    const value = rawValue.join(':').trim();

    const isAllowed =
      ALLOWED_STYLE_PROPERTIES.includes(property) &&
      value !== '' &&
      !REFUSED_STYLE_VALUE_FRAGMENTS.some((fragment) => value.toLowerCase().includes(fragment));

    if (isAllowed) {
      kept.push(`${property}: ${value}`);
    }
  }

  if (kept.length === 0) {
    element.removeAttribute('style');
    return;
  }

  element.setAttribute('style', `${kept.join('; ')};`);
}

function convertQuillIndentToInlineMargin(element: Element) {
  const indentClass = Array.from(element.classList).find((name) =>
    QUILL_INDENT_CLASS_REGEX.test(name)
  );
  if (isNil(indentClass) || !(element instanceof HTMLElement)) {
    return;
  }

  const level = Number(QUILL_INDENT_CLASS_REGEX.exec(indentClass)?.[1]);
  element.classList.remove(indentClass);
  element.style.marginLeft = `${level * QUILL_INDENT_WIDTH_IN_EM}em`;

  if (element.classList.length === 0) {
    element.removeAttribute('class');
  }
}

interface SemanticListGroup {
  tagName: 'ul' | 'ol';
  items: Element[];
}

// Quill 2 serializes every list as `<ol>` and marks bullet items with `data-list="bullet"`, which
// only its own stylesheet turns into bullets. Emails need real `<ul>` runs.
function convertQuillListToSemanticLists(list: HTMLOListElement) {
  const items = Array.from(list.children);

  if (!items.some((item) => item.hasAttribute('data-list'))) {
    return;
  }

  const groups: SemanticListGroup[] = [];

  items.forEach((item) => {
    const tagName = item.getAttribute('data-list') === 'bullet' ? 'ul' : 'ol';
    item.removeAttribute('data-list');

    const lastGroup = last(groups);
    if (lastGroup?.tagName === tagName) {
      lastGroup.items.push(item);
      return;
    }

    groups.push({tagName, items: [item]});
  });

  list.replaceWith(
    ...groups.map((group) => {
      const semanticList = list.ownerDocument.createElement(group.tagName);
      semanticList.append(...group.items);
      return semanticList;
    })
  );
}
